import React from 'react';
import { FaMousePointer } from 'react-icons/fa';
import {
  tooltipStyle,
  fixedPanelStyle,
  titleStyle,
  dividerStyle,
  contentStyle,
  statusStyle,
  subcategoriesContainerStyle,
  subcategoriesHeaderStyle,
  subcategoriesCountStyle,
  subcategoryItemStyle,
  bulletPointStyle,
  relatedLocationsStyle,
  relatedLocationsHeaderStyle,
  relatedLocationItemStyle,
  moreItemsStyle,
  emptyStateStyle,
  emptyStateIconStyle,
  projectsContainerStyle,
  projectsHeaderStyle,
  projectsCountStyle,
  projectItemStyle,
  emptyProjectsStyle
} from './styles';

const MAX_ITEMS_TO_SHOW = 20;

const EmptyState = () => (
  <div style={emptyStateStyle}>
    <FaMousePointer style={emptyStateIconStyle} />
    <div>Hover over an element to learn more</div>
  </div>
);

const Tooltip = ({ 
  data, 
  position, 
  approvalStageColors,
  isVisible = false,
  isFixed = false
}) => {
  const containerStyle = isFixed ? fixedPanelStyle : {
    ...tooltipStyle,
    left: `${position.x}px`,
    top: `${position.y}px`,
  };

  if (isFixed && !data) {
    return (
      <div style={containerStyle}>
        <EmptyState />
      </div>
    );
  }

  if (!isVisible || !data) return null;

  const renderSubcategories = () => {
    if (!data.subcategories?.length) return null;

    const totalItems = data.subcategories.length;
    const itemsToShow = data.subcategories.slice(0, MAX_ITEMS_TO_SHOW);
    const remainingItems = totalItems - MAX_ITEMS_TO_SHOW;

    return (
      <div style={subcategoriesContainerStyle}>
        <div style={subcategoriesHeaderStyle}>
          <span style={subcategoriesCountStyle}>
            {totalItems}
          </span>
          {' Elements in this segment'}
        </div>
        <div style={dividerStyle} />
        {itemsToShow.map((subcat, index) => (
          <div key={index} style={subcategoryItemStyle}>
            <span style={bulletPointStyle}>•</span>
            {subcat.name}
          </div>
        ))}
        {remainingItems > 0 && (
          <div style={moreItemsStyle}>
            + {remainingItems} more elements...
          </div>
        )}
      </div>
    );
  };

  const renderProjects = () => {
    return (
      <div style={projectsContainerStyle}>
        {data.projects?.length ? (
          <>
            <div style={projectsHeaderStyle}>
              <span style={projectsCountStyle}>
                {data.projects.length}
              </span>
              {' Associated Projects'}
            </div>
            <div style={dividerStyle} />
            {data.projects.map((project, index) => (
              <div key={index} style={projectItemStyle}>
                <span style={bulletPointStyle}>•</span>
                {project.name}
                {project.code && ` (${project.code})`}
              </div>
            ))}
          </>
        ) : (
          <div style={emptyProjectsStyle}>
            No projects associated
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <div style={{
        ...titleStyle,
        textTransform: data.subcategories?.length > 0 ? 'uppercase' : 'none'
      }}>
        {data.name}
      </div>
      
      <div style={dividerStyle} />
      
      <div style={contentStyle}>
        {data.approvalStage && (
          <div style={{
            ...statusStyle,
            color: approvalStageColors[data.approvalStage.toUpperCase()]
          }}>
            Status: {data.approvalStage}
          </div>
        )}
        
        {data.tags && data.tags.length > 0 && (
          <div style={projectsContainerStyle}>
            <div style={projectsHeaderStyle}>
              <span style={projectsCountStyle}>
                {data.tags.length}
              </span>
              {' Tags'}
            </div>
            <div style={dividerStyle} />
            {data.tags.map((tag, index) => (
              <div key={index} style={projectItemStyle}>
                <span style={bulletPointStyle}>•</span>
                {tag.name}
              </div>
            ))}
          </div>
        )}
        
        {data.projects && renderProjects()}
        
        {renderSubcategories()}
        
        {data.relatedLocations && (
          <div style={relatedLocationsStyle}>
            <div style={relatedLocationsHeaderStyle}>
              Also appears in:
            </div>
            {data.relatedLocations.map((location, index) => (
              <div 
                key={index}
                style={{
                  ...relatedLocationItemStyle,
                  color: location.color || '#2B4B81'
                }}
              >
                {location.path.join(' > ')}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tooltip; 